// src/components/bot/catalogCache.js
// ─────────────────────────────────────────────────────────────────────────────
// Supporting Layer — Endpoint Catalog Cache
//
// Responsibilities:
//   • Fetch the endpoint catalog from GET /chat-boat exactly once per session.
//   • Recursively flatten the nested menu tree into a flat array of
//     { name, endpoint } entries.
//   • Provide getCatalogText() for the AI Planner's prompt.
//   • Provide getFlat() for the API Executor's label lookup.
//
// Design principle: the backend /chat-boat response is the SINGLE source of
// truth for all available endpoints. Nothing is hardcoded here.
// ─────────────────────────────────────────────────────────────────────────────
import { botFetch } from './botApi';

// Module-level session cache — null = not fetched, [] = fetched but empty
let _catalog = null; // Array<{ name: string, endpoint: string }>

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Recursively flatten the nested menu tree returned by /chat-boat.
 *
 * The backend returns a tree like:
 *   [
 *     { id: 1, name: "Attendance", data: [
 *       { id: 11, name: "Today's Attendance", endpoint: "chat-boat/today-attendance" },
 *       { id: 12, name: "Date-wise Attendance", endpoint: "chat-boat/date-attendance" }
 *     ]},
 *     { id: 2, name: "Employees", endpoint: "chat-boat/employee-count?id=..." },
 *     ...
 *   ]
 *
 * We only keep entries that have an endpoint (leaf nodes).
 *
 * @param {Array}  items   - Menu tree nodes
 * @param {Array}  result  - Accumulator (mutated in place)
 * @returns {Array<{ name: string, endpoint: string }>}
 */
function flattenMenu(items, result = []) {
  if (!Array.isArray(items)) return result;

  for (const item of items) {
    // Leaf node — has an endpoint → keep it
    if (item.endpoint) {
      const name = (item.name || item.title || item.label || 'Unnamed').trim();
      const endpoint = item.endpoint.trim();
      result.push({
        name,
        endpoint,
        _searchString: `${name} ${endpoint}`.toLowerCase(),
      });
    }

    // Branch node — recurse into children
    if (Array.isArray(item.data) && item.data.length > 0) {
      flattenMenu(item.data, result);
    }
  }

  return result;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Warm up the catalog cache by fetching /chat-boat.
 * Safe to call multiple times — subsequent calls return the cached value
 * instantly without making another network request.
 *
 * @returns {Promise<Array<{ name: string, endpoint: string }>>}
 */
export async function warmUp() {
  if (_catalog !== null) return _catalog;

  try {
    const r = await botFetch('/chat-boat');
    if (!r.ok) {
      console.warn('[CatalogCache] /chat-boat returned an error:', r.message);
      _catalog = [];
      return _catalog;
    }

    // The response may be a top-level array or wrapped in .json / .data
    const raw = Array.isArray(r.data)
      ? r.data
      : (r.data?.json || r.data?.data || []);

    _catalog = flattenMenu(raw);

    if (_catalog.length === 0) {
      console.warn('[CatalogCache] /chat-boat returned an empty menu tree.');
    }
  } catch (e) {
    console.warn('[CatalogCache] Unexpected error fetching catalog:', e.message);
    _catalog = [];
  }

  return _catalog;
}

/**
 * Return the flat catalog array.
 * Returns an empty array if warmUp() has not been called yet.
 */
export function getFlat() {
  return _catalog ?? [];
}

/**
 * Build a human-readable text representation of the catalog for use
 * in the AI Planner prompt.
 *
 * Example output:
 *   - "Today's Attendance" → endpoint: chat-boat/today-attendance
 *   - "Employee Count" → endpoint: chat-boat/employee-count?id=...
 *
 * @returns {string}
 */
export function getCatalogText() {
  const flat = getFlat();
  if (!flat.length) return '(no endpoints available — catalog could not be loaded)';
  return flat.map(e => `- "${e.name}" → endpoint: ${e.endpoint}`).join('\n');
}

/**
 * Score endpoints against user message keywords to aggressively reduce prompt size.
 *
 * @param {string} userMessage - The raw user input
 * @param {number} limit - Max number of candidates to return
 * @returns {string} - The catalog text formatted for Gemini
 */
export function getTopCandidates(userMessage, limit = 15) {
  const flat = getFlat();
  if (!flat.length) return '(no endpoints available — catalog could not be loaded)';

  // 1. Extract alphanumeric lowercase keywords
  const keywords = (userMessage || '')
    .toLowerCase()
    .split(/[\s,?.!]+/)
    .filter(w => w.length > 2); // ignore tiny words like "a", "is", "to"

  if (keywords.length === 0) {
    // Fallback if no keywords found: return the first N endpoints
    return flat.slice(0, limit).map(e => `- "${e.name}" → endpoint: ${e.endpoint}`).join('\n');
  }

  // 2. Score endpoints
  const scored = flat.map(entry => {
    let score = 0;
    for (const kw of keywords) {
      if (entry._searchString.includes(kw)) score++;
    }
    return { ...entry, score };
  });

  // 3. Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // 4. Return top N as formatted text
  return scored
    .slice(0, limit)
    .map(e => `- "${e.name}" → endpoint: ${e.endpoint}`)
    .join('\n');
}

/** Reset cache (primarily for testing). Not called in normal usage. */
export function resetCatalog() {
  _catalog = null;
}
